import { FC, useEffect, useState } from 'react';
import { Modal } from '../../../../../components/modal';
import Switch from '../../../../../components/switch';
import {
  ISettingsResponse,
  ISubChild,
  ISubSubChild,
} from '../../../../../interface/settings';
import { useUpdateSettingsData } from '../../../../../services/settings';
import { capitalized } from '../../../../../utils/capitalize';
import { useToast } from '../../../../../context/toast';
import Charges from './charge';

export interface IAirtime {
  settingsData?: ISettingsResponse;
  refetch: () => void;
}

const Airtime: FC<IAirtime> = ({ settingsData, refetch }) => {
  const airtimeData = settingsData?.isp.airtime;
  const [toggleItems, setToggleItems] = useState<ISubChild[]>(
    airtimeData?.data!
  );
  const [active, setActive] = useState<boolean>(airtimeData?.active ?? false);
  const { mutate, isLoading } = useUpdateSettingsData();
  const { addToast } = useToast();
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<ISubChild>({} as ISubChild);
  const [charge, setCharge] = useState<string>(
    airtimeData?.charge.toString() ?? ''
  );

  const handleMutationSuccess = () => {
    refetch();
    addToast({ msg: 'Successfully Updated', type: 'success' });
  };

  const toggleChange = () => {
    setActive(!active);
    mutate(
      {
        isp: {
          ...settingsData?.isp!,
          airtime: {
            active: !airtimeData?.active,
            data: toggleItems,
          },
        },
      },
      {
        onSuccess: handleMutationSuccess,
      }
    );
  };

  const handleAirtimeChange = (selected: ISubChild) => {
    const activeItem = toggleItems.find((item) => item.name === selected.name);
    if (activeItem) {
      setToggleItems((prevToggleItems) =>
        prevToggleItems.map((obj) =>
          obj === activeItem ? { ...obj, visibility: !obj.visibility } : obj
        )
      );
    }
    mutate(
      {
        isp: {
          ...settingsData?.isp!,
          airtime: {
            ...airtimeData,
            data: airtimeData?.data.map((item) =>
              item.name === selected.name
                ? { ...selected, visibility: !selected.visibility }
                : item
            ),
          },
        },
      },
      {
        onSuccess: handleMutationSuccess,
      }
    );
  };

  const submitCharges = () => {
    mutate(
      {
        isp: {
          ...settingsData?.isp!,
          airtime: {
            ...settingsData?.isp.airtime,
            charge: Number(charge),
          },
        },
      },
      {
        onSuccess: handleMutationSuccess,
      }
    );
  };

  const handleSubAirtimeChange = (selected: ISubSubChild) => {
    setEditItem({
      ...editItem,
      provider: editItem.provider.map((provider) =>
        provider.name === selected.name
          ? { ...provider, active: !provider.active }
          : provider
      ),
    });
    mutate(
      {
        isp: {
          ...settingsData?.isp!,
          airtime: {
            ...airtimeData,
            data: airtimeData?.data.map((item) => {
              const updatedProviders = item.provider.map((provider) =>
                provider.name === selected.name
                  ? { ...provider, active: !provider.active }
                  : provider
              );
              return { ...item, provider: updatedProviders };
            }),
          },
        },
      },
      {
        onSuccess: () => {
          handleMutationSuccess();
        },
      }
    );
  };

  useEffect(() => {
    setActive(airtimeData?.active || false);
    setToggleItems(airtimeData?.data || []);
  }, [airtimeData]);

  return (
    <div className="md:w-96 w-full text-sm">
      <div className="mb-6 bg-transparent border border-grey px-2 py-3 rounded-md flex justify-between items-center">
        <h5>Activate All Airtime</h5>
        <Switch checked={active} onCheck={toggleChange} />
      </div>

      <div className="rounded-md bg-dark mb-6">
        <Charges
          setCharge={setCharge}
          charge={charge}
          submit={submitCharges}
          loading={isLoading}
        />
      </div>

      {toggleItems?.map((item: ISubChild, index: number) => (
        <div key={index}>
          <div className="mb-2 bg-dark px-2 py-3 rounded-md flex justify-between items-center">
            <h5>Activate {capitalized(item.name)}</h5>
            <div className="flex items-center">
              <div
                className="mr-5 cursor-pointer"
                onClick={() => {
                  setOpen(true);
                  setEditItem(item);
                }}
              >
                Edit
              </div>
              <span className="max-w-[150px] w-full grid place-items-center">
                <Switch
                  checked={item.visibility}
                  onCheck={() => handleAirtimeChange(item)}
                />
              </span>
            </div>
          </div>
        </div>
      ))}

      {open && editItem && (
        <Modal
          isVisible={open}
          close={() => {
            setOpen(false);
            setEditItem({} as ISubChild);
          }}
          title={capitalized(editItem.name)}
          padding={false}
          center
          header
          width="medium"
        >
          {editItem?.provider.map((item: ISubSubChild, index: number) => (
            <div key={index}>
              <div className="py-4 px-4 flex justify-between items-center text-xs">
                <div>{capitalized(item.name)}</div>
                <div className="flex items-center">
                  {item.rate !== null && (
                    <div className="bg-bColor py-1 px-2 gap-2 text-xs rounded-md flex mr-5">
                      <h5>Rate:</h5>
                      <span>{`${item.rate}%`}</span>
                    </div>
                  )}
                  <span className="max-w-[150px] w-full flex justify-end">
                    <Switch
                      checked={item.active}
                      onCheck={() => handleSubAirtimeChange(item)}
                    />
                  </span>
                </div>
              </div>
              {index !== editItem.provider.length - 1 && (
                <hr className="border-grey/30" />
              )}
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
};

export default Airtime;
